import { DetailViewModel } from "@frui.ts/datascreens/dist";
import { bound } from "@frui.ts/helpers/dist";
import { Router, watchBusy } from "@frui.ts/screens/dist";
import { attachAutomaticValidator, getValidationMessage, validateAll } from "@frui.ts/validation/dist";
import type { interfaces } from "inversify";
import cloneDeep from "lodash/cloneDeep";
import toUpper from "lodash/toUpper";
import { computed, observable, toJS } from "mobx";
import OptionsService from "services/optionsService";
import { Modules, newNavigationName } from "config";
import LicensePlate, { LicensePlateValidationRules } from "entities/licensePlate";
import ExemptionRepository from "repositories/exemptionRepository";
import EnumService from "services/enumService";
import EventBus, { exemptionCreated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import { endOfCzechDay, startOfCzechDay } from "utils/dateUtils";
import formatCountryEnumName from "utils/formatCountryEnumName";
import CreateExemptionRequest, { CreateExemptionValidationRules } from "entities/createExemptionRequest";
import CountrySelectViewModel from "viewModels/countrySelectViewModel";
import ExemptionPreviewResponse from "entities/exemptionPreviewResponse";
import * as Sentry from "@sentry/react";
import ExemptionCreationRejectionReason from "entities/exemptionCreationRejectionReason";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ route: newNavigationName, name: Router.Self })
export default class CreateExemptionViewModel extends DetailViewModel<CreateExemptionRequest> {
  private disposers: Array<() => void> = [];
  private createExemptionPreview: ExemptionPreviewResponse;
  countrySelectViewModel: CountrySelectViewModel;
  @observable creationStep = 1;
  @observable loading = false;

  constructor(
    private repository: ExemptionRepository,
    private localizationService: LocalizationService,
    private eventBus: EventBus,
    private notificationService: NotificationService,
    private optionsService: OptionsService,
    private enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    private originalLicensePlate?: LicensePlate
  ) {
    super();
    this.navigationName = newNavigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize(): Promise<void> {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.disposers.forEach(dispose => dispose());
      this.disposers = [];
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get officialRecordId() {
    return this.item.officialRecordId;
  }

  set officialRecordId(recordNumber: string) {
    this.item.officialRecordId = toUpper(recordNumber);
  }

  @computed
  get officialRecordIdError() {
    return getValidationMessage(this.item, "officialRecordId");
  }

  @computed
  get exemptionPreview() {
    return this.createExemptionPreview;
  }

  @bound
  isContinueDisabled() {
    return !!this.createExemptionPreview?.rejectionReason;
  }

  @computed
  get countries() {
    return this.enumService.values("countries").map(country => ({ id: country.id, label: formatCountryEnumName(country) }));
  }

  protected loadDetail() {
    const entity = new CreateExemptionRequest();
    entity.vehicle = cloneDeep(this.originalLicensePlate) ?? new LicensePlate();
    entity.validSinceDate = startOfCzechDay(new Date());

    attachAutomaticValidator(entity, CreateExemptionValidationRules);
    attachAutomaticValidator(entity.vehicle, LicensePlateValidationRules);
    return entity;
  }

  @computed
  get exemptionReasonOptions() {
    return this.optionsService.enumOptions("notifiable_exemption_reasons", (item: any) => ({
      id: item.id,
      label: item.description,
    }));
  }

  @bound
  getExemptionReasonId(exemptionReason: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === exemptionReason)?.id;
  }

  @bound
  getExemptionReason(id: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === id)?.description;
  }

  @bound
  @watchBusy
  onBack() {
    this.creationStep = 1;
    this.createExemptionPreview = new ExemptionPreviewResponse();
  }

  @bound
  @watchBusy
  async onSave() {
    if (this.item.validSinceDate) {
      this.item.validSinceDate = startOfCzechDay(this.item.validSinceDate);
    }

    if (this.item.validUntilDate) {
      this.item.validUntilDate = endOfCzechDay(this.item.validUntilDate);
    }

    const requestObj = cloneDeep(toJS(this.item));

    if (this.item.exemptionReasonId) {
      requestObj.exemptionReasonId = this.getExemptionReasonId(this.item.exemptionReasonId) ?? "";
    }

    if (validateAll([this.item, this.item.vehicle]) && this.creationStep === 2) {
      try {
        await this.repository.create(requestObj);
        this.eventBus.publish(exemptionCreated({ entity: requestObj }));
        this.notificationService.add({ title: this.translate("created"), type: NotificationType.SUCCESS });
      } catch (e) {
        Sentry.captureException(e);
      }
      this.creationStep = 1;
      this.loading = false;
      this.requestClose();
      return;
    }
    if (validateAll([this.item, this.item.vehicle]) && this.creationStep === 1) {
      this.loading = true;
      try {
        this.createExemptionPreview = await this.repository.createPreview(requestObj);
        this.creationStep = 2;
      } catch (e) {
        Sentry.captureException(e);
      }
      this.loading = false;
    }
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @computed
  get rejectedReasonText() {
    if (!this.exemptionPreview?.rejectionReason) {
      return undefined;
    }

    const reason = this.exemptionPreview?.rejectionReason;

    if (reason === ExemptionCreationRejectionReason.ExemptionAlreadyExists) {
      return this.translatePreview("exemptionAlreadyExists");
    }
    return this.translatePreview("warningBypassingForbidden");
  }

  @computed
  get rejectedReason() {
    return !!this.rejectedReasonText;
  }

  @computed
  get warningReasons() {
    if (!this.exemptionPreview?.warningReasons) {
      return undefined;
    }
    return this.exemptionPreview?.warningReasons;
  }

  translatePreview(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `createExemption.${code}`, size);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `createExemption.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  @bound
  translateCountryId(countryId: string) {
    if (!countryId) {
      return emptyValue;
    }
    const country = this.enumService.value("countries", countryId);

    if (!country) {
      return emptyValue;
    }
    return `${country.code} - ${country.name}`;
  }

  translateAttribute(model: "exemption", attribute: keyof CreateExemptionRequest, size?: number): string;
  translateAttribute(model: "licensePlate", attribute: keyof LicensePlate, size?: number): string;
  translateAttribute(
    model: "exemption" | "licensePlate",
    attribute: keyof CreateExemptionRequest | keyof LicensePlate,
    size?: number
  ) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }

  translateClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  static Factory({ container }: interfaces.Context) {
    return (licensePlate?: LicensePlate) =>
      new CreateExemptionViewModel(
        container.get(ExemptionRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(EnumService),
        container.get(CountrySelectViewModel.Factory),
        licensePlate
      );
  }
}
